import { useState } from 'react';
import { FiChevronRight } from 'react-icons/fi';
import { Box, Button, Flex } from './base';

// Os visuais pequenos dos cartões de baixo do painel: o medidor (uma parte de
// um todo), a barra dividida (a parte de cada um) e as colunas (a venda por
// família). Só desenham proporções; todo número escrito vem do servidor.
// As cores são as do painel (visual.ts): a cor dos dados e a paleta
// categórica, validada para daltonismo nos dois temas.

// Meio círculo com a parte preenchida: "41% da venda nos 5 maiores clientes".
export function Medidor({
  percentual,
  valor,
  legenda,
  rotulo,
}: {
  percentual: number;
  valor: string;
  legenda: string;
  rotulo: string;
}) {
  const p = Math.max(0, Math.min(percentual, 100)) / 100;
  // Arco de 180° com raio 80, centro (100, 100).
  const comprimento = Math.PI * 80;
  return (
    <Box position="relative" w="100%" maxW="240px" mx="auto" role="img" aria-label={`${rotulo}: ${valor}`}>
      <svg viewBox="0 0 200 110" width="100%" aria-hidden="true">
        <path d="M 20 100 A 80 80 0 0 1 180 100" fill="none" stroke="var(--pa-grade)" strokeWidth="14" strokeLinecap="round" />
        <path
          d="M 20 100 A 80 80 0 0 1 180 100"
          fill="none"
          stroke="var(--pa-dado)"
          strokeWidth="14"
          strokeLinecap="round"
          strokeDasharray={`${comprimento * p} ${comprimento}`}
        />
      </svg>
      <Box position="absolute" left={0} right={0} bottom="2px" textAlign="center">
        <Box fontSize="26px" lineHeight="32px" fontWeight={700} letterSpacing="-0.02em">
          {valor}
        </Box>
        <Box fontSize="12px" lineHeight="16px" color="var(--pa-tinta-3)">
          {legenda}
        </Box>
      </Box>
    </Box>
  );
}

export interface Parte {
  nome: string;
  valor: number;
  texto: string;
  extra?: React.ReactNode;
}

// Uma barra só, dividida na parte de cada um, e a legenda embaixo em duas
// colunas. Na barra, os que passam de `maximo` viram "Outros", em cinza. Com
// `todos`, a legenda traz cada um pelo nome (os de fora da barra com o ponto
// cinza da fatia deles), os primeiros e "Ver todos os N"; sem ele, "Outros".
const LEGENDA_DE_INICIO = 10;

export function BarraDividida({
  partes,
  cores,
  maximo = 5,
  outros,
  todos,
}: {
  partes: Parte[];
  cores: string[];
  maximo?: number;
  outros?: Parte;
  todos?: Parte[];
}) {
  const [aberta, setAberta] = useState(false);
  const lista = partes.slice(0, maximo);
  const resto = outros && outros.valor > 0 ? outros : undefined;
  const total = [...lista, ...(resto ? [resto] : [])].reduce((s, p) => s + Math.max(p.valor, 0), 0) || 1;
  const cor = (i: number) => (i < maximo ? cores[i % cores.length] : 'var(--pa-linha)');
  const legenda = todos?.length
    ? todos.map((p, i) => ({ ...p, cor: cor(i) }))
    : [...lista.map((p, i) => ({ ...p, cor: cor(i) })), ...(resto ? [{ ...resto, cor: 'var(--pa-linha)' }] : [])];
  const visiveis = aberta ? legenda : legenda.slice(0, LEGENDA_DE_INICIO);
  return (
    <Box>
      <Flex h="10px" borderRadius="5px" overflow="hidden" sx={{ gap: '3px' }} role="img" aria-label={lista.map((p) => `${p.nome}: ${p.texto}`).join('; ')}>
        {lista.map((p, i) => (
          <Box key={p.nome} flex={`${Math.max(p.valor, 0) / total} 0 0`} minW="4px" bg={cor(i)} borderRadius="3px" />
        ))}
        {resto && <Box flex={`${resto.valor / total} 0 0`} minW="4px" bg="var(--pa-linha)" borderRadius="3px" />}
      </Flex>
      <Box as="ul" listStyleType="none" m={0} p={0} mt="14px" display="grid" gridTemplateColumns="repeat(2, minmax(0, 1fr))" sx={{ gap: '10px 20px' }}>
        {visiveis.map((p, i) => (
          <Flex as="li" key={`${p.nome}-${i}`} align="center" sx={{ gap: '8px' }} fontSize="13px" minW={0}>
            <Box as="i" w="8px" h="8px" borderRadius="50%" bg={p.cor} flexShrink={0} />
            <Box flex="1" minW={0} overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap" title={p.nome} color="var(--pa-tinta-2)">
              {p.nome}
            </Box>
            <Box fontWeight={600} flexShrink={0}>
              {p.texto}
            </Box>
          </Flex>
        ))}
      </Box>
      {legenda.length > LEGENDA_DE_INICIO && (
        <Flex justify="center" mt="10px">
          <Button variant="ghost" size="sm" fontSize="13px" color="var(--pa-tinta-2)" aria-expanded={aberta} onClick={() => setAberta(!aberta)}>
            {aberta ? 'Mostrar menos' : `Ver todos os ${legenda.length}`}
          </Button>
        </Flex>
      )}
    </Box>
  );
}

// Colunas verticais: a parte de cada família na venda, com o percentual em cima
// e o nome embaixo. A coluna mais alta usa a cor dos dados; as outras, a mesma
// cor apagada, para o maior saltar aos olhos.
export function Colunas({ partes, altura = 150 }: { partes: Parte[]; altura?: number }) {
  const maior = Math.max(...partes.map((p) => Math.abs(p.valor)), 1);
  return (
    <Box
      as="ol"
      listStyleType="none"
      m={0}
      p={0}
      display="grid"
      gridTemplateColumns={`repeat(${partes.length}, minmax(0, 1fr))`}
      sx={{ gap: '8px' }}
    >
      {partes.map((p, i) => (
        <Flex as="li" key={p.nome} direction="column" align="stretch" minW={0}>
          {/* O percentual vai colado no topo da barra, na altura dela. */}
          <Flex h={`${altura}px`} direction="column" justify="flex-end">
            <Box fontSize="12px" fontWeight={700} textAlign="center" mb="4px" whiteSpace="nowrap">
              {p.texto}
            </Box>
            <Box
              w="100%"
              h={`${Math.max((Math.abs(p.valor) / maior) * (altura - 22), 3)}px`}
              borderRadius="6px 6px 2px 2px"
              bg={i === 0 ? 'var(--pa-dado)' : 'var(--pa-dado-fantasma)'}
            />
          </Flex>
          {/* Nome longo não quebra no meio da palavra: corta, e o inteiro fica na dica. */}
          <Box
            mt="6px"
            fontSize="11px"
            lineHeight="14px"
            color="var(--pa-tinta-3)"
            textAlign="center"
            title={p.nome}
            whiteSpace="nowrap"
            overflow="hidden"
            textOverflow="ellipsis"
          >
            {p.nome}
          </Box>
          {p.extra && (
            <Box mt="2px" textAlign="center">
              {p.extra}
            </Box>
          )}
        </Flex>
      ))}
    </Box>
  );
}

// ─── Barras horizontais ──────────────────────────────────────────────────────
// Uma barra por linha, na mesma escala (uma unidade só por gráfico), dividida
// em partes. O número escrito à direita vem do servidor; a tela só desenha a
// proporção. A linha ligada a um alerta é um botão que o abre.

export interface Segmento {
  nome: string;
  valor: number;
  cor: string;
  // A dica do pedaço, com números que vieram prontos.
  dica?: string;
}

export interface LinhaDeBarras {
  chave: string;
  rotulo: string;
  segmentos: Segmento[];
  texto: React.ReactNode;
  dica?: string;
  onClick?: () => void;
  // Linha à parte (recuada e em tom mais claro): fora da conta da de cima.
  aParte?: boolean;
}

export function BarrasHorizontais({
  linhas,
  legenda,
  rotuloDoGrafico,
}: {
  linhas: LinhaDeBarras[];
  legenda?: { nome: string; cor: string }[];
  rotuloDoGrafico: string;
}) {
  const total = (l: LinhaDeBarras) => l.segmentos.reduce((s, p) => s + Math.max(p.valor, 0), 0);
  const maior = Math.max(...linhas.map(total), 1);
  return (
    <Box>
      {legenda && legenda.length > 1 && (
        <Flex wrap="wrap" sx={{ gap: '4px 14px' }} px="16px" pb="8px" fontSize="12px" color="var(--pa-tinta-2)">
          {legenda.map((l) => (
            <Flex key={l.nome} align="center" sx={{ gap: '6px' }}>
              <Box as="i" w="10px" h="10px" borderRadius="3px" bg={l.cor} />
              {l.nome}
            </Flex>
          ))}
        </Flex>
      )}
      <Box as="ul" listStyleType="none" m={0} p={0} aria-label={rotuloDoGrafico}>
        {linhas.map((l) => {
          const soma = total(l);
          const conteudo = (
            <>
              <Box
                w={{ base: '112px', md: '168px' }}
                flexShrink={0}
                pl={l.aParte ? '12px' : 0}
                fontSize={l.aParte ? '12px' : '13px'}
                fontWeight={l.aParte ? 500 : 600}
                color={l.aParte ? 'var(--pa-tinta-2)' : 'var(--pa-tinta)'}
                lineHeight="17px"
              >
                {l.rotulo}
              </Box>
              <Flex flex="1" minW={0} h="12px" align="center">
                {soma > 0 ? (
                  <Flex h="100%" w={`${Math.max((soma / maior) * 100, 1.5)}%`} sx={{ gap: '2px' }}>
                    {l.segmentos
                      .filter((s) => s.valor > 0)
                      .map((s) => (
                        <Box key={s.nome} title={s.dica} h="100%" flex={`${s.valor} 0 0`} minW="3px" bg={s.cor} borderRadius="4px" />
                      ))}
                  </Flex>
                ) : (
                  <Box h="2px" w="100%" bg="var(--pa-grade)" borderRadius="1px" />
                )}
              </Flex>
              <Box w={{ base: '92px', md: '118px' }} flexShrink={0} textAlign="right" fontSize="13px" lineHeight="17px">
                {l.texto}
              </Box>
              <Box as="span" w="16px" flexShrink={0} color="var(--pa-tinta-3)" aria-hidden="true">
                {l.onClick && <FiChevronRight size={16} />}
              </Box>
            </>
          );
          const comum = {
            display: 'flex',
            alignItems: 'center',
            sx: { gap: '12px' },
            w: '100%',
            px: '16px',
            py: l.aParte ? '7px' : '10px',
            title: l.dica,
          };
          return (
            <Box as="li" key={l.chave} borderTop={l.aParte ? undefined : '1px solid var(--pa-grade)'}>
              {l.onClick ? (
                <Box
                  as="button"
                  type="button"
                  onClick={l.onClick}
                  textAlign="left"
                  bg="transparent"
                  border="none"
                  color="inherit"
                  cursor="pointer"
                  _hover={{ bg: 'var(--pa-superficie-2)' }}
                  _focusVisible={{ outline: '2px solid var(--pa-dado)', outlineOffset: '-2px' }}
                  {...comum}
                >
                  {conteudo}
                </Box>
              ) : (
                <Box {...comum}>{conteudo}</Box>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
